/**
 * Shared deck validation for constructed and limited modes.
 * Rules: Riftbound Tournament Rules 401-403, 601-602
 */

export const CONSTRUCTED_MAIN_SIZE = 40;
export const CONSTRUCTED_RUNES = 12;
export const CONSTRUCTED_BATTLEFIELDS = 3;
export const CONSTRUCTED_SIDEBOARD_MAX = 8;
export const MAX_COPIES_PER_CARD = 3;

export const LIMITED_MAIN_SIZE = 25;
export const LIMITED_RUNES = 12;
export const LIMITED_BATTLEFIELDS = 3;
// In limited (Standard / draft), sideboard is the remainder of the pool, so no strict max.
export const LIMITED_SIDEBOARD_MAX = Infinity;

const COLOURLESS_DOMAINS = new Set(['colourless', 'colorless']);

export function parseDomain(domain) {
  if (!domain) return [];
  if (typeof domain === 'string') {
    return domain.split(',').map((d) => d.trim().toLowerCase()).filter(Boolean);
  }
  if (Array.isArray(domain)) {
    return domain
      .map((d) => (typeof d === 'string' ? d : d?.label || String(d)))
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
}

export function countCopiesByName(deck, cardName) {
  if (!cardName) return 0;
  const name = String(cardName).toLowerCase();
  let count = 0;
  if (deck.champion?.name?.toLowerCase() === name) count++;
  for (const c of deck.mainDeck || []) {
    if (c?.name?.toLowerCase() === name) count++;
  }
  for (const c of deck.sideboard || []) {
    if (c?.name?.toLowerCase() === name) count++;
  }
  return count;
}

export function hasBattlefieldWithName(deck, name) {
  if (!name) return false;
  const n = String(name).toLowerCase();
  const battlefields = deck.battlefields || [];
  return battlefields.some((c) => (c?.name || '').toLowerCase() === n);
}

export function cardMatchesDomainIdentity(card, domainIdentity) {
  if (!domainIdentity?.length) return true;
  const cardDomains = parseDomain(card?.domain);
  if (cardDomains.length === 0) return true;
  if (cardDomains.every((d) => COLOURLESS_DOMAINS.has(d))) return true;
  const identitySet = new Set(domainIdentity.map((d) => d.toLowerCase()));
  return cardDomains.every((d) => COLOURLESS_DOMAINS.has(d) || identitySet.has(d));
}

/** Legend and Champion share at least one tag (e.g. "Sett"). */
export function championMatchesLegend(legend, champion) {
  if (!legend || !champion) return true;
  const legendTags = new Set(
    (legend.tags?.tags || legend.tags || [])
      .map((t) => (typeof t === 'string' ? t : t?.id || t?.label || '').toLowerCase())
      .filter(Boolean)
  );
  const championTags = new Set(
    (champion.tags?.tags || champion.tags || [])
      .map((t) => (typeof t === 'string' ? t : t?.id || t?.label || '').toLowerCase())
      .filter(Boolean)
  );
  for (const t of championTags) {
    if (legendTags.has(t)) return true;
  }
  return false;
}

/** Signature spell: constructed = must match legend tags; limited = domain only. */
export function isSignatureSpellValid(card, legend, mode = 'constructed') {
  const type = (card?.cardType?.type?.[0]?.label || card?.type || '').toLowerCase();
  if (type !== 'spell') return true;
  const isSignature = card?.isSignatureSpell || card?.signatureSpell;
  if (!isSignature) return true;
  if (mode === 'limited') return true;
  return championMatchesLegend(legend, card);
}

/** Compute domain identity for deck: legend + champion domains, max 3. */
export function computeDomainIdentity(deck, mode = 'constructed') {
  const legend = deck.legend;
  const champion = deck.champion;
  const domains = new Set();
  if (legend) {
    for (const d of parseDomain(legend.domain)) {
      if (!COLOURLESS_DOMAINS.has(d)) domains.add(d);
    }
  }
  if (champion && mode === 'constructed') {
    for (const d of parseDomain(champion.domain)) {
      if (!COLOURLESS_DOMAINS.has(d)) domains.add(d);
    }
  }
  const arr = [...domains].slice(0, 3);
  return arr;
}

export function getDeckErrors(deck, mode = 'constructed') {
  const errors = [];
  const { champion, mainDeck = [], sideboard = [], runes = [], battlefields = [] } = deck;
  const isLimited = mode === 'limited';
  const targetMainSize = isLimited ? LIMITED_MAIN_SIZE : CONSTRUCTED_MAIN_SIZE;
  const targetRunes = isLimited ? LIMITED_RUNES : CONSTRUCTED_RUNES;
  const targetBattlefields = isLimited ? LIMITED_BATTLEFIELDS : CONSTRUCTED_BATTLEFIELDS;
  const sideboardMax = isLimited ? LIMITED_SIDEBOARD_MAX : CONSTRUCTED_SIDEBOARD_MAX;

  const mainCount = mainDeck.length + (champion ? 1 : 0);
  if (mainCount !== targetMainSize) {
    errors.push(`Main deck must be ${targetMainSize} cards (currently ${mainCount})`);
  }
  const runeCount = (runes || []).reduce((sum, r) => sum + (r?.quantity ?? 1), 0);
  if (runeCount !== targetRunes) {
    errors.push(`Rune deck must be ${targetRunes} runes (currently ${runeCount})`);
  }
  if (battlefields.length !== targetBattlefields) {
    errors.push(`Must have ${targetBattlefields} battlefields (currently ${battlefields.length})`);
  }
  if (!isLimited && sideboard.length > sideboardMax) {
    errors.push(`Sideboard max is ${sideboardMax} cards (currently ${sideboard.length})`);
  }

  const bfNames = battlefields.map((c) => (c?.name || '').toLowerCase()).filter(Boolean);
  if (new Set(bfNames).size !== bfNames.length) {
    errors.push('Battlefields must have unique names');
  }

  if (!isLimited) {
    const allCards = [champion, ...mainDeck, ...sideboard].filter(Boolean);
    const nameCounts = new Map();
    for (const c of allCards) {
      const name = (c?.name || '').toLowerCase();
      if (name) nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
    }
    for (const [name, count] of nameCounts) {
      if (count > MAX_COPIES_PER_CARD) {
        errors.push(`Max ${MAX_COPIES_PER_CARD} copies of "${name}" (have ${count})`);
      }
    }
  }

  return errors;
}

/** Return set of card IDs that are illegal (domain/copy limit/signature). */
export function getDeckIllegal(deck, mode = 'constructed') {
  const illegal = new Set();
  const identity = computeDomainIdentity(deck, mode);
  const { legend, champion, mainDeck = [], sideboard = [], battlefields = [] } = deck;

  const checkCard = (card) => {
    if (!card?.id) return;
    if (!cardMatchesDomainIdentity(card, identity)) illegal.add(card.id);
    if (mode === 'constructed' && card.id !== champion?.id && !isSignatureSpellValid(card, legend, mode)) {
      illegal.add(card.id);
    }
    if (mode === 'constructed' && countCopiesByName(deck, card.name) > MAX_COPIES_PER_CARD) {
      illegal.add(card.id);
    }
  };

  if (champion && mode === 'constructed' && !championMatchesLegend(legend, champion)) {
    if (champion.id) illegal.add(champion.id);
  }

  for (const c of mainDeck) checkCard(c);
  for (const c of sideboard) checkCard(c);

  const bfNames = new Set();
  for (const c of battlefields) {
    const name = (c?.name || '').toLowerCase();
    if (name && bfNames.has(name)) illegal.add(c.id);
    if (name) bfNames.add(name);
  }

  return illegal;
}
